// The shared render passes (research-artAudio §2 "Render passes", UX §6.2). Every sprite is a list of
// parts; drawParts runs, in order:
//   1. glow (tier ≥ glowMinTier): the silhouette with shadowBlur (no ctx.filter — Safari);
//   2. sticker halo (loose items): one stroke of the whole silhouette, cream at haloAlpha;
//   3. outline: every part stroked at 2× the outline width UNDER the fills, so only the outer silhouette
//      outline remains and parts show no seams;
//   4. per part: base fill, then (clipped to the part) the shade band over the bottom 35%, the warm rim
//      light on the upper left (tier ≥ rimMinTier) and the top highlight crescent, then the part's details.
// Units: generators draw in cell units (100 = one cell); line widths are in the same units.
import type { Ctx2D } from './ctx.ts';
import type { ArtPalette, Swatch } from './palette.ts';
import { type Box, moved, type PathFn } from './shapes.ts';

export interface Part {
  path: PathFn;
  sw: Swatch;
  /** Local bounds: the shade band covers its bottom `shadeBand` share. */
  box: Box;
  /** No shade band / highlight / rim (e.g. the blaze tip, flat decals). */
  flat?: boolean;
  shade?: boolean;
  hi?: boolean;
  /** Outline colour override (default: the swatch outline). */
  outline?: string;
  noOutline?: boolean;
  /** Drawn right after this part's fill (later parts cover it). */
  detail?: (c: Ctx2D) => void;
}

export interface PassOpts {
  /** Visible outline width in units. */
  ow: number;
  rim?: boolean;
  halo?: { w: number; color: string; alpha: number } | null;
  /** rgba colour and blur in DEVICE pixels (shadowBlur ignores the transform). */
  glow?: { color: string; blur: number } | null;
  shadeBand: number;
  /** Highlight crescent thickness in units (clamped per part). */
  hiW?: number;
}

/** Fills (region of `path`) minus (path moved by dx,dy): a crescent along the opposite edges. */
function crescent(c: Ctx2D, path: PathFn, b: Box, dx: number, dy: number, color: string): void {
  c.beginPath();
  c.rect(b.x - 50, b.y - 50, b.w + 100, b.h + 100);
  moved(path, dx, dy)(c);
  c.fillStyle = color;
  c.fill('evenodd');
}

export function drawParts(c: Ctx2D, parts: readonly Part[], o: PassOpts): void {
  c.lineJoin = 'round';
  c.lineCap = 'round';
  if (o.glow) {
    c.save();
    c.shadowColor = o.glow.color;
    c.shadowBlur = o.glow.blur;
    c.shadowOffsetX = 0;
    c.shadowOffsetY = 0;
    c.fillStyle = o.glow.color;
    c.beginPath();
    for (const p of parts) p.path(c);
    c.fill();
    c.restore();
  }
  if (o.halo) {
    c.save();
    c.globalAlpha = o.halo.alpha;
    c.strokeStyle = o.halo.color;
    c.fillStyle = o.halo.color;
    c.lineWidth = 2 * (o.ow + o.halo.w);
    c.beginPath();
    for (const p of parts) p.path(c);
    c.stroke();
    c.fill();
    c.restore();
  }
  for (const p of parts) {
    if (p.noOutline) continue;
    c.beginPath();
    p.path(c);
    c.lineWidth = 2 * o.ow;
    c.strokeStyle = p.outline ?? p.sw.outline;
    c.stroke();
  }
  for (const p of parts) drawPartFill(c, p, o);
}

export function drawPartFill(c: Ctx2D, p: Part, o: PassOpts): void {
  c.beginPath();
  p.path(c);
  c.fillStyle = p.sw.base;
  c.fill();
  if (!p.flat) {
    const b = p.box;
    c.save();
    c.beginPath();
    p.path(c);
    c.clip();
    if (p.shade !== false) {
      const h = b.h * o.shadeBand;
      c.fillStyle = p.sw.shade;
      c.fillRect(b.x - 2, b.y + b.h - h, b.w + 4, h + 2);
    }
    const t = Math.min(o.hiW ?? 3, Math.min(b.w, b.h) * 0.2);
    if (o.rim) crescent(c, p.path, b, t, t * 0.4, p.sw.light);
    if (p.hi !== false) crescent(c, p.path, b, 0, t, p.sw.hi);
    c.restore();
  }
  p.detail?.(c);
}

/** A part from a path + swatch. */
export function part(path: PathFn, sw: Swatch, b: Box, extra: Partial<Part> = {}): Part {
  return { path, sw, box: b, ...extra };
}

/** Outline width in units for a frame of `px` device pixels per cell (5% of the cell, ≥ minPx CSS px). */
export function outlineUnits(frac: number, minCssPx: number, px: number, dpr: number): number {
  return Math.max(frac * 100, ((minCssPx * dpr) / px) * 100);
}

/** Detail stroke (bolts, seams, stencils): half the outline width, in the outline colour. */
export function detailStroke(c: Ctx2D, path: PathFn, color: string, w: number): void {
  c.beginPath();
  path(c);
  c.strokeStyle = color;
  c.lineWidth = w;
  c.lineJoin = 'round';
  c.lineCap = 'round';
  c.stroke();
}

export function fillP(c: Ctx2D, path: PathFn, color: string): void {
  c.beginPath();
  path(c);
  c.fillStyle = color;
  c.fill();
}

/** A contact-shadow-free sticker helper for tokens: the glow options for tier t, or null. */
export function glowFor(
  pal: ArtPalette,
  tierTok: string,
  tier: number,
  px: number,
): { color: string; blur: number } | null {
  if (tier < pal.rules.glowMinTier) return null;
  return { color: pal.rgba(tierTok, pal.art.glowAlpha, 'glow'), blur: pal.art.glowBlur * px };
}
